/**
 * The catalogue, as the site knows it.
 *
 * Two halves, because the facts have two different sources.
 *
 * `printed.json` is a *snapshot*, written by site/scripts/archive-book.ts at
 * the moment a book is archived and then left alone. It is not derived at build
 * time and must never be: the generator moves — raising the Easy band from
 * technique level 1 to 2 changed what a given seed produces, so a seed no
 * longer reproduces a book already in someone's hands. Anything the site claims
 * about a printed book has to come from a record made when it was printed. The
 * archive script will not quietly rewrite a record it already holds.
 *
 * `SALES` is the other half, and it is typed by hand because nothing else
 * knows it. An ASIN and a publication date exist only after KDP says so, which
 * is well after the book was built. A book with no entry here is archived but
 * not yet on sale: the site serves its puzzles and its help page, and shows no
 * buy button, because there is nowhere to send anyone yet.
 *
 * Books are grouped into *lines* rather than listed flat, and a line owns its
 * own page. The press publishes logic puzzles today and is not promising to
 * stop there, so a guarantee that only holds for logic grids — one solution,
 * no guessing, minimal clues — belongs on the line's page and nowhere near the
 * front of the site. A second line should be a new entry in `LINES` plus a
 * route, not a rewrite of anything.
 */
import type { LogicGridDesignation } from "@gen/logic-grid/types.ts";
import printed from "./printed.json";

/** A product line — a family of books that share a premise and a promise. */
export interface Line {
  id: string;
  name: string;
  /** Where the line's own page lives. */
  href: string;
  /** One sentence on what the line is, for the catalogue heading. */
  summary: string;
}

export const LINES: Line[] = [
  {
    id: "logic-grid",
    name: "Logic grid puzzles",
    href: "/logic-grids",
    summary:
      "Match people to things using a list of clues. One solution, always reachable without guessing, and one difficulty per book.",
  },
];

export function lineById(id: string): Line | undefined {
  return LINES.find((l) => l.id === id);
}

export interface Book {
  /** URL-safe id. Also the archive folder name: books/<slug>/. */
  slug: string;
  /** Which line in `LINES` this belongs to. */
  line: string;
  title: string;
  subtitle: string;
  /**
   * The difficulty printed on the cover. Typed from the generator rather than
   * restated, so adding a tier there is a compile error here until this file
   * agrees.
   */
  designation: LogicGridDesignation;
  puzzleCount: number;
  /** Trim size as printed on the cover. */
  trim: string;
  binding: string;
  /** Interior page count, once the printed proof confirms it. */
  pages?: number;
  /** Publication date, YYYY-MM-DD. */
  published?: string;
  /**
   * Amazon ASIN, once there is one. Absent means archived but not yet on sale —
   * the state every book is in between being built and being listed, which is
   * where the whole shelf sits while it is uploaded a book at a time. Callers
   * must handle it: `amazonUrl` returns undefined and the buy button is not
   * drawn.
   */
  asin?: string;
  /**
   * Whether books/<slug>/ holds the archived puzzles this book was printed
   * from. Only an archived book can offer hints — see components/HelpForm.tsx.
   */
  archived: boolean;
  blurb: string;
  /** What a buyer gets, for the book's own page. */
  contents: string[];
}

/**
 * What only KDP knows, keyed by slug. Add a line when a book goes on sale.
 *
 * The one book listed here went up before the shelf existed; the rest are
 * archived and waiting. Its ASIN is the only thing in this file that was not
 * true the moment the book was built.
 */
const SALES: Record<string, { asin?: string; published?: string }> = {
  "logic-puzzles-introduction": { asin: "B0HDYVGJX1" },
};

/**
 * Every archived book, in the order the archive recorded them.
 *
 * The snapshot carries what was printed; `SALES` carries what was sold. The
 * cast is the one place the two shapes meet, and `printed.json` is written by a
 * script that types its output as `Book`, so the shapes agree at the point they
 * are produced.
 */
export const BOOKS: Book[] = (printed as Omit<Book, "asin" | "published">[]).map((book) => ({
  ...book,
  ...SALES[book.slug],
}));

/** Where a book is bought, or undefined while it is not yet on sale. */
export function amazonUrl(book: Book): string | undefined {
  return book.asin ? `https://www.amazon.com/dp/${book.asin}` : undefined;
}

/** Whether there is somewhere to send a buyer. */
export function onSale(book: Book): boolean {
  return book.asin != null;
}

/**
 * The widths site/scripts/book-covers.ts emits, smallest first.
 *
 * Named here rather than in the script because the markup has to agree with
 * what is on disk, and only one of the two can be the source.
 */
export const COVER_WIDTHS = [320, 640] as const;

/** A cover at one width. */
export function coverSrc(book: Book, width: number): string {
  return `/covers/${book.slug}-${width}.png`;
}

/**
 * Both widths as a srcset, so a phone and a 2x book page pick their own.
 *
 * By convention rather than by a flag on Book, for the reason `asin` is not
 * nullable: every book in this list is on sale, and KDP does not sell a book
 * without a cover. A missing file is a build that should not have shipped, so
 * scripts/prerender.ts checks for them and refuses rather than letting a broken
 * image reach the catalogue.
 */
export function coverSrcSet(book: Book): string {
  return COVER_WIDTHS.map((w) => `${coverSrc(book, w)} ${w}w`).join(", ");
}

/**
 * A book named as briefly as it can be and still be picked out of a list.
 *
 * Title alone will not do: there are six titles across forty-eight books, one
 * per format. Title plus subtitle will not do either — the subtitle restates
 * the word the title already ends on, so the honest form of it is "Logic
 * Puzzles: Easy Puzzles", and the Introduction's runs to a clause with a
 * separator in it. The designation is the half that actually distinguishes one
 * book from another, which is the same reason the slug is built from it.
 */
export function bookLabel(book: Book): string {
  return `${book.title} · ${book.designation}`;
}

/** What a screen reader should say. The image is the book, not decoration. */
export function coverAlt(book: Book): string {
  return `Front cover of ${book.title}${book.subtitle ? `: ${book.subtitle}` : ""}`;
}

export function bookBySlug(slug: string): Book | undefined {
  return BOOKS.find((b) => b.slug === slug);
}

export function booksInLine(line: string): Book[] {
  return BOOKS.filter((b) => b.line === line);
}

/** Books whose printed puzzles are archived, so the site can offer hints. */
export function archivedBooks(): Book[] {
  return BOOKS.filter((b) => b.archived);
}
